import { useMemo, useRef, useState } from "react";
import { HandHeart, Save, FileDown, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { fmtNum, fmtDate } from "@/lib/format";
import { elementToPdf } from "@/lib/pdf";
import { BigButton, Field, inputCls } from "@/components/ui-kit";
import { bankStats } from "@/pages/Bank";

const HAWL_DAYS = 354;
const RATE = 0.025;

export function zakatCalc(shop, products, customers, bank = 0) {
  const stock = Object.values(products).reduce((s, p) => s + Math.max(0, p.stock || 0) * Number(p.cost || p.price || 0), 0);
  const receivables = Object.values(customers).reduce((s, c) => s + Math.max(0, c.balance || 0), 0);
  const cash = Number(shop.zakat_cash || 0);
  const other = Number(shop.zakat_other || 0);
  const debts = Number(shop.zakat_debts || 0);
  const wealth = Math.max(0, stock + receivables + cash + other + bank - debts);
  const nisab = Number(shop.zakat_nisab || 0);
  const start = shop.zakat_hawl_start ? new Date(shop.zakat_hawl_start) : null;
  const elapsed = start ? Math.floor((Date.now() - start.getTime()) / 86400000) : 0;
  const daysLeft = start ? Math.max(0, HAWL_DAYS - elapsed) : HAWL_DAYS;
  const reachesNisab = nisab > 0 && wealth >= nisab;
  const complete = !!start && daysLeft === 0;
  const due = reachesNisab ? Math.round(wealth * RATE * 100) / 100 : 0;
  return { stock, receivables, cash, other, debts, bank, wealth, nisab, start, daysLeft, reachesNisab, complete, due, progress: start ? Math.min(100, Math.round((elapsed / HAWL_DAYS) * 100)) : 0 };
}

const Row = ({ label, value, bold, tone, testid }) => (
  <div className={`flex justify-between gap-3 py-1.5 border-b border-dashed border-[#CCCCCC] last:border-0 ${bold ? "font-bold" : ""}`} style={{ color: tone }}>
    <span>{label}</span><span dir="ltr" data-testid={testid}>{value}</span>
  </div>
);

export default function ZakatPanel() {
  const { t, view, dispatch, money, lang, can } = useApp();
  const shop = view.shop || {};
  const [f, setF] = useState({ zakat_nisab: shop.zakat_nisab ?? "", zakat_cash: shop.zakat_cash ?? "", zakat_other: shop.zakat_other ?? "", zakat_debts: shop.zakat_debts ?? "" });
  const ref = useRef(null);
  const z = useMemo(() => zakatCalc({ ...shop, ...f }, view.products, view.customers, bankStats(view).balance), [shop, f, view]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = (extra = {}) => {
    const data = { zakat_nisab: Number(f.zakat_nisab || 0), zakat_cash: Number(f.zakat_cash || 0), zakat_other: Number(f.zakat_other || 0), zakat_debts: Number(f.zakat_debts || 0), ...extra };
    if (dispatch("shop.update", data)) toast.success(t("zakat_saved"));
  };
  const today = new Date().toISOString().slice(0, 10);
  const status = !z.reachesNisab ? ["zakat_below", "#555555"] : z.complete ? ["zakat_complete", "#1F9D55"] : ["zakat_days_left", "#B9651A"];

  return (
    <section className="rounded-2xl bg-white border border-border p-4 ak-card" data-testid="zakat-panel">
      <div className="flex items-center gap-2 mb-1"><HandHeart className="w-5 h-5 text-[#1F9D55]" /><h3 className="font-heading font-bold text-lg">{t("zakat")}</h3></div>
      <p className="text-sm text-muted-foreground mb-4">{t("zakat_desc")}</p>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <Field label={t("zakat_nisab")}><input dir="ltr" inputMode="decimal" value={f.zakat_nisab} onChange={set("zakat_nisab")} className={inputCls} data-testid="zakat-nisab-input" /></Field>
          <Field label={t("zakat_cash")}><input dir="ltr" inputMode="decimal" value={f.zakat_cash} onChange={set("zakat_cash")} className={inputCls} data-testid="zakat-cash-input" /></Field>
          <Field label={t("zakat_other")}><input dir="ltr" inputMode="decimal" value={f.zakat_other} onChange={set("zakat_other")} className={inputCls} data-testid="zakat-other-input" /></Field>
          <Field label={t("zakat_debts")}><input dir="ltr" inputMode="decimal" value={f.zakat_debts} onChange={set("zakat_debts")} className={inputCls} data-testid="zakat-debts-input" /></Field>
          <Field label={t("zakat_hawl_start")}>
            <div className="flex gap-2">
              <input type="date" dir="ltr" value={shop.zakat_hawl_start || ""} onChange={(e) => can("settings") && dispatch("shop.update", { zakat_hawl_start: e.target.value })} className={inputCls} data-testid="zakat-hawl-input" />
              <BigButton variant="soft" onClick={() => save({ zakat_hawl_start: today })} data-testid="zakat-set-start-btn"><CalendarCheck className="w-5 h-5" />{t("zakat_set_start")}</BigButton>
            </div>
          </Field>
          {can("settings") && <BigButton onClick={() => save()} className="w-full" data-testid="zakat-save-btn"><Save className="w-5 h-5" />{t("save")}</BigButton>}
        </div>
        <div className="space-y-3">
          <div ref={ref} className="rounded border border-[#CCCCCC] bg-white p-4 text-sm" data-testid="zakat-report">
            <div className="text-center font-bold text-base border-b border-dashed border-[#CCCCCC] pb-2 mb-2">{t("zakat")} · {shop.name}<div className="text-xs font-normal text-[#666666]">{fmtDate(new Date().toISOString(), lang)}</div></div>
            <Row label={t("zakat_stock")} value={money(z.stock)} testid="zakat-stock" />
            <Row label={t("zakat_receivables")} value={money(z.receivables)} testid="zakat-receivables" />
            <Row label={t("zakat_cash")} value={money(z.cash)} />
            <Row label={t("virtual_bank")} value={money(z.bank)} testid="zakat-bank" />
            <Row label={t("zakat_other")} value={money(z.other)} />
            <Row label={t("zakat_debts")} value={`- ${money(z.debts)}`} />
            <Row label={t("zakat_wealth")} value={money(z.wealth)} bold testid="zakat-wealth" />
            <Row label={t("zakat_nisab")} value={money(z.nisab)} />
            {z.start && <Row label={t("zakat_hawl_start")} value={`${fmtDate(z.start.toISOString(), lang)} · ${z.progress}%`} testid="zakat-progress" />}
            <div className="h-2 rounded bg-[#E8E8E8] my-2 overflow-hidden"><div className="h-full bg-[#1F9D55] transition-[width]" style={{ width: `${z.progress}%` }} /></div>
            <div className="text-xs font-bold" style={{ color: status[1] }} data-testid="zakat-status">{t(status[0], { n: z.daysLeft })}</div>
            <div className="mt-3 rounded bg-[#F0F0F0] border border-[#CCCCCC] p-3 flex justify-between items-center">
              <span className="font-bold">{z.complete ? t("zakat_due") : t("zakat_projected")} ({t("zakat_rate")})</span>
              <span dir="ltr" className="text-2xl font-extrabold" style={{ color: z.complete && z.due > 0 ? "#1F9D55" : "#333333" }} data-testid="zakat-due">{money(z.due)}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <BigButton variant="soft" onClick={() => elementToPdf(ref.current, `zakat-${today}.pdf`, { heading: t("zakat") })} data-testid="zakat-pdf-btn"><FileDown className="w-5 h-5" />PDF</BigButton>
            {can("settings") && <BigButton variant="green" onClick={() => save({ zakat_hawl_start: today })} disabled={!z.complete} data-testid="zakat-reset-btn"><HandHeart className="w-5 h-5" />{t("zakat_reset")}</BigButton>}
          </div>
        </div>
      </div>
    </section>
  );
}
